import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import '../../App.scss';
import 'antd/dist/antd.css';
import {
  Alert, Button, Grid, Space, Spin, Table, Tag, Timeline, Typography, message, Tooltip, Input,
} from 'antd';
import { PlayCircleOutlined, ExportOutlined } from '@ant-design/icons';
import moment from 'moment';

import { useTerminals, useTerminalHistory, useTeamOwners } from '../../models/useTerminals';

const { Text } = Typography;
const { useBreakpoint } = Grid;

const fmt = (ts) => (ts && typeof ts.toDate === 'function' ? moment(ts.toDate()).format('YYYY-MM-DD HH:mm') : '—');

/** Hardware lozenge, matching the Smart POS device cards. */
const snLozenge = (sn) => (
  <span style={{
    display: 'inline-block',
    padding: '1px 8px',
    borderRadius: '10px',
    background: '#F4EEE2',
    border: '1px solid #E3D9C2',
    color: '#6B5D42',
    fontSize: '12px',
    whiteSpace: 'nowrap',
  }}
  >
    {`SN ${String(sn).slice(-4).toUpperCase()}`}
  </span>
);

/** Biography wording + dot color per event — the same timeline grammar as
 * the withdrawals progress view (PayoutProgressListView). */
const EVENT_PRESENTATION = {
  registered: { color: 'green', title: () => 'Registered — first seen by the fleet' },
  bound: { color: 'blue', title: (e) => `Bound to ${e.teamName || e.teamId || 'a team'}` },
  unbound: { color: 'red', title: (e) => `Unbound from ${e.teamName || e.teamId || 'its team'}` },
  'mode-changed': {
    color: 'purple',
    title: (e) => (e.mode === 'tip-kiosk' ? 'Always on enabled' : 'Always on turned off'),
  },
  'kiosk-armed': { color: 'orange', title: (e) => (e.by === 'support' ? 'Kiosk screen restarted by support' : 'Kiosk screen armed') },
  // Connectivity transitions (strike-payer #387): written only when Stripe's
  // reader status flips, so each row is a real outage edge, not a tick.
  'went-offline': { color: 'red', title: (e) => `Went offline${e.by ? ` (seen by ${e.by})` : ''}` },
  'went-online': { color: 'green', title: (e) => `Back online${e.by ? ` (seen by ${e.by})` : ''}` },
  // The arm lifecycle (strike-payer #400, 16 Oct): Stripe's word and the
  // glass disagreed, and the server settled it.
  'standing-screen-dead': {
    color: 'orange',
    title: (e) => `Dead standing screen replaced${Number.isFinite(e.ageMs) ? ` (${Math.round(e.ageMs / 60000)} min old)` : ''}`,
  },
  'armed-after-timeout': { color: 'green', title: () => 'Screen confirmed up after a Stripe timeout' },
};

/** Stripe's reader.action types in plain words — the fleet shows what the glass is doing, not an API noun. */
const readerActionLabel = (type) => ({
  collect_inputs: 'screen',
  process_payment_intent: 'card screen',
  set_reader_display: 'display',
  cancel_action: 'cleared',
}[type] || type);
/** …and its status: a screen is up, answered or ended — never "in_progress". */
const readerActionState = (status) => ({
  in_progress: 'up',
  succeeded: 'answered',
  failed: 'ended',
}[status] || status || '');

/**
 * Stripe's reason, cut to what a human needs (Charles, 15 Oct: "the errors
 * are off-putting and HUGE"). The stored string is "code: message" where
 * the message carries a sentence of boilerplate pointing at the integration
 * guide. Keep the code and the first clause; drop everything from the first
 * "please…" / "Reference the integration guide" / second sentence on.
 */
export const shortReaderReason = (reason) => {
  const raw = String(reason || '').trim();
  if (!raw) return { code: null, text: 'failed' };
  const colon = raw.indexOf(':');
  const code = colon > 0 && !/\s/.test(raw.slice(0, colon)) ? raw.slice(0, colon) : null;
  let text = code ? raw.slice(colon + 1).trim() : raw;
  const cut = text.search(/,?\s*(please|Please)\b|\s*Reference the integration guide|\.\s/);
  if (cut > 0) text = text.slice(0, cut);
  text = text.replace(/[.,;\s]+$/, '');
  return { code, text: text || code || 'failed' };
};

/** The server's KIOSK_STANDING_SCREEN_MAX_MS, in minutes: a standing screen
 * "up" for longer is one the reader never drew (strike-payer #400). */
const STANDING_SCREEN_MAX_MIN = 3;

/** Firestore Timestamp, Date or ISO string → epoch ms (NaN when absent). */
const ms = (t) => {
  if (!t) return NaN;
  if (typeof t.toMillis === 'function') return t.toMillis();
  if (typeof t.toDate === 'function') return t.toDate().getTime();
  return new Date(t).getTime();
};
const asMoment = (t) => (Number.isNaN(ms(t)) ? null : moment(ms(t)));
/** "Mon 18:13" — when something stopped. */
const since = (t) => { const m = asMoment(t); return m ? m.format('ddd HH:mm') : '—'; };
/** "2 minutes ago" — how fresh something is. */
const ago = (t) => { const m = asMoment(t); return m ? m.fromNow() : '—'; };

/**
 * ONE state per device, in words (Charles, 15 Oct: "simplify this
 * screen"). Mode, Stripe's connectivity, what the glass is showing and the
 * latest arm failure collapse into a lozenge Ops can read at a glance, and a
 * severity the table sorts by: offline first, then anything needing
 * attention, then never-seen, then healthy. An arm failure counts only
 * while it is the latest word — once a later arm succeeded it is history.
 */
export const deviceState = (row) => {
  const failure = row.lastArmFailedAt && !(row.lastArmedAt && ms(row.lastArmedAt) > ms(row.lastArmFailedAt))
    ? shortReaderReason(row.lastArmFailedReason) : null;
  const kiosk = row.mode === 'tip-kiosk';
  const mode = kiosk ? 'Always on' : 'Hand-off';
  if (row.status && row.status !== 'active') {
    return { severity: 1, color: 'default', text: `${mode} · ${row.status}`, failure };
  }
  const connectivity = row.readerStatus || 'unknown';
  // Stripe no longer knows the reader id on this tag (deleted during a
  // device move, or another account) — strike-payer stamps it once and
  // stops asking. Needs a human: rebind the serial or take the tag off live.
  if (connectivity === 'missing') {
    return { severity: 1, color: 'orange', text: 'Reader not in Stripe', failure };
  }
  if (connectivity === 'offline') {
    return { severity: 0, color: 'red', text: `Offline${row.readerSeenAt ? ` since ${since(row.readerSeenAt)}` : ''}`, failure };
  }
  if (connectivity !== 'online') {
    return { severity: failure ? 1 : 2, color: 'default', text: `${mode} · not yet seen`, failure };
  }
  // ONLINE. Green means "taking tips": a card screen up, a screen up, or a
  // hand-off device ready (Charles, 16 Oct: the fleet was all red and
  // purple with no green for healthy). A failure line rides under a green
  // lozenge when the glass is fine regardless — a Stripe timeout on a
  // restart that the reader drew anyway is history, not a state.
  // The freshest evidence wins: a put-up that Stripe accepted AFTER the last
  // reading means the screen is up, whatever the older reading said (16 Oct:
  // a row read "no screen" for five minutes after every re-put, because only
  // the sweep's reading said what was on the glass).
  const putUpAfterReading = kiosk && !failure
    && !Number.isNaN(ms(row.lastArmedAt)) && ms(row.lastArmedAt) > (ms(row.readerCheckedAt) || 0);
  const actionUp = row.readerActionStatus === 'in_progress'
    ? row.readerActionType : (putUpAfterReading ? 'collect_inputs' : null);
  if (actionUp === 'process_payment_intent') {
    return { severity: 3, color: 'green', text: `${mode} · card screen up`, failure };
  }
  if (actionUp && row.readerActionSession === true) {
    // A customer mid-flow (tip choice, custom amount, summary): the age
    // counts from the put-up, not from their screen, so it is not shown —
    // and the 3-minute rule is the attract screen's and the pads', not theirs.
    return { severity: 3, color: 'green', text: `${mode} · customer on screen`, failure };
  }
  if (actionUp) {
    // A STANDING screen cannot be real past three minutes (an untouched one
    // times out at two, a touched one ends on the answer): older is a screen
    // the reader never drew — attention until the sweep replaces it (#400),
    // or a slow customer on a session screen, which the age also says.
    const armedMs = ms(row.lastArmedAt);
    const ageMin = Number.isNaN(armedMs) ? null : Math.floor((Date.now() - armedMs) / 60000);
    if (kiosk && ageMin !== null && ageMin > STANDING_SCREEN_MAX_MIN) {
      return { severity: 1, color: 'orange', text: `${mode} · ${readerActionLabel(actionUp)} up ${ageMin} min`, failure };
    }
    return { severity: 3, color: 'green', text: `${mode} · ${readerActionLabel(actionUp)} up`, failure };
  }
  if (kiosk) {
    // Always on with nothing standing: asleep outside hours, or between a
    // screen's end and the next arm — not healthy, not an outage. A refusal
    // on top is what makes it attention.
    return { severity: failure ? 1 : 2, color: failure ? 'orange' : 'default', text: 'Always on · no screen', failure };
  }
  return { severity: 3, color: 'green', text: 'Hand-off · ready', failure };
};

/** "17 terminals · 14 online · 3 offline · 2 need attention" */
export const fleetSummary = (rows) => {
  const states = rows.map(deviceState);
  const online = rows.filter((r) => r.readerStatus === 'online').length;
  const offline = states.filter((st) => st.severity === 0).length;
  const attention = states.filter((st) => st.severity === 1).length;
  return [
    `${rows.length} terminal${rows.length === 1 ? '' : 's'}`,
    online ? `${online} online` : null,
    offline ? `${offline} offline` : null,
    attention ? `${attention} need${attention === 1 ? 's' : ''} attention` : null,
  ].filter(Boolean).join(' · ');
};


const PREVIEW_EVENTS = 8;

/** One expanded row's live history PREVIEW — newest first (audit reading
 * order), culled to the last few with a click-through to the full record. */
const TerminalHistoryRows = ({ serialNumber }) => {
  const [loading, events, error] = useTerminalHistory(serialNumber);
  if (loading) return <Spin size="small" />;
  if (error) return <Text type="danger">{error}</Text>;
  if (events.length === 0) return <Text type="secondary">No history yet</Text>;
  const preview = events.slice(0, PREVIEW_EVENTS);
  return (
    <>
      <Timeline pending={null} mode="right" style={{ maxWidth: '640px', margin: '8px auto' }}>
        {preview.map((event) => {
          const presentation = EVENT_PRESENTATION[event.event] || { color: 'gray', title: () => event.event };
          const specific = event.event === 'bound' || event.event === 'registered'
            ? (event.deviceTagId || event.tagId || '') : '';
          return (
            <Timeline.Item key={event.id} color={presentation.color} label={fmt(event.at)}>
              <p style={{ margin: 0, fontWeight: 'bold' }}>{presentation.title(event)}</p>
              <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                {`${specific ? `${specific} · ` : ''}by ${event.byName || event.by || '—'}`}
              </p>
            </Timeline.Item>
          );
        })}
      </Timeline>
      {events.length > PREVIEW_EVENTS && (
        <div style={{ textAlign: 'center' }}>
          <Link to={`/admin/ops/terminals/${serialNumber}`}>
            {`View full history (${events.length}${events.length >= 50 ? '+' : ''}) →`}
          </Link>
        </div>
      )}
    </>
  );
};

/**
 * Ops → Terminals: the terminals/{SN} fleet registry, newest activity first.
 * Data + intents come from useTerminals(); the registry is written
 * server-side only — this screen renders and asks.
 */
const AdminAllTerminalsBody = ({ isAdmin }) => {
  const [loading, terminals, error, { startKiosk, sweepAll }] = useTerminals();
  const [armingSn, setArmingSn] = useState(null);
  const [sweeping, setSweeping] = useState(false);
  // The cron's sweep on demand (Charles, 15 Oct): after a deploy, screens
  // lost to the 503 window waited for the next tick. Same sweep, same
  // guards — a reader read first, never over a screen in use.
  const onSweepAll = async () => {
    setSweeping(true);
    try {
      const result = await sweepAll();
      if (result.error) {
        message.error(`Sweep failed: ${result.error}`);
        return;
      }
      const counts = Object.entries(result.counts || {})
        .map(([outcome, n]) => `${n} ${outcome}`).join(', ');
      message.success(`Swept ${result.swept} Always-on device${result.swept === 1 ? '' : 's'}${counts ? ` — ${counts}` : ''}`);
    } catch (err) {
      message.error('Sweep request failed');
    } finally {
      setSweeping(false);
    }
  };
  // A hundred rows by end of next week (Charles, 14 Oct): grouped by team by
  // default, searchable, and the team name jumps to the team's admin page.
  const [query, setQuery] = useState('');
  const owners = useTeamOwners(terminals.map((row) => row.teamId));
  const teamKey = (row) => String(row.teamName || row.teamId || '').toLowerCase();
  const needle = query.trim().toLowerCase();
  const rows = needle
    ? terminals.filter((row) => [row.teamName, row.teamId, row.sn, row.tmr, row.deviceTagId]
      .some((field) => String(field || '').toLowerCase().includes(needle)))
    : terminals;
  const teamLinks = (row) => {
    const owner = owners[row.teamId];
    if (!owner || !row.teamId) return <Text strong>{row.teamName || row.teamId || '—'}</Text>;
    const base = `/admin/clients/${owner}/teams/${row.teamId}`;
    return (
      <Space size={6}>
        <Link to={base}><Text strong>{row.teamName || row.teamId}</Text></Link>
        <Link to={`${base}/pos`} style={{ fontSize: '11px' }}>POS</Link>
      </Space>
    );
  };
  const screens = useBreakpoint();
  // Rule 10: only an EXPLICIT false is compact — {} on the first tick stays desktop.
  const compact = screens.md === false;

  const onStartKiosk = async (row) => {
    setArmingSn(row.sn);
    try {
      const { outcome, reason } = await startKiosk(row);
      if (outcome === 'armed') message.success(`Kiosk screen armed on ${row.sn.slice(-4)}`);
      else {
        // Each guard gets ITS OWN words — one hardcoded parenthetical once
        // dressed a reader 400 as a mode problem (2026-10-02 incident).
        const explain = {
          'not-kiosk': 'device must be in Always on mode',
          'not-live': 'the device tag is not live',
          'outside-hours': 'outside the team\u2019s opening hours',
          'no-values': 'the team has no tip presets',
          'members-only': 'the team tips members only \u2014 Always on needs pooled tipping',
          locked: 'a live session holds the device',
          'too-soon': 'just armed \u2014 wait a few seconds',
          'arm-failed': 'the reader refused the screen',
          // The sweep's reader read (strike-payer #389) and the thank-you
          // hold (#390) — the glass is busy, nothing was posted.
          offline: 'Stripe reports the reader offline',
          'payment-in-flight': 'a card screen is up on the reader',
          pending: 'the standing screen is still up and in use',
          'finishing-up': 'the last customer is still on the receipt screen',
          missing: 'Stripe no longer knows this reader id \u2014 rebind the serial or take the tag off live',
        };
        const detail = reason || explain[outcome] || 'see server logs';
        message.warning(`Not armed: ${outcome} (${detail})`);
      }
    } catch (err) {
      message.error('Arm request failed');
    } finally {
      setArmingSn(null);
    }
  };

  const stripeReaderUrl = (row) => `https://dashboard.stripe.com/${row.is_test ? 'test/' : ''}terminal/readers/${row.tmr}`;

  // Default order: what needs a human first — offline, then attention, then
  // never seen, then healthy — and inside a band grouped by team, then
  // serial. The Team header still sorts by team alone when asked.
  const ordered = [...rows].sort((a, b) => deviceState(a).severity - deviceState(b).severity
    || teamKey(a).localeCompare(teamKey(b)) || String(a.sn).localeCompare(String(b.sn)));

  const stateCell = (row) => {
    const st = deviceState(row);
    return (
      <Space direction="vertical" size={0}>
        <Tag color={st.color} style={{ margin: 0 }}>{st.text}</Tag>
        {st.failure && (
          <Tooltip title={row.lastArmFailedReason || ''}>
            <Text type="danger" style={{ fontSize: '11px' }}>
              {`${st.failure.code ? `${st.failure.code} — ` : ''}${st.failure.text}`}
            </Text>
          </Tooltip>
        )}
      </Space>
    );
  };
  const activityCell = (row) => (
    <Tooltip title={[
      row.lastArmedAt && `armed ${fmt(row.lastArmedAt)}${row.lastArmedBy ? ` by ${row.lastArmedBy}` : ''}`,
      row.readerSeenAt && `seen ${fmt(row.readerSeenAt)}`,
      row.readerCheckedAt && `checked ${fmt(row.readerCheckedAt)}`,
    ].filter(Boolean).join(' · ')}
    >
      <Text type="secondary" style={{ fontSize: '12px' }}>
        {[
          row.lastArmedAt && `armed ${ago(row.lastArmedAt)}${row.lastArmedBy ? ` by ${row.lastArmedBy}` : ''}`,
          row.readerSeenAt && `seen ${ago(row.readerSeenAt)}`,
        ].filter(Boolean).join(' · ') || '—'}
      </Text>
    </Tooltip>
  );

  const columns = [
    {
      title: 'Team',
      key: 'team',
      sorter: (a, b) => teamKey(a).localeCompare(teamKey(b)) || String(a.sn).localeCompare(String(b.sn)),
      sortDirections: ['ascend', 'descend'],
      render: (row) => (
        <Space direction="vertical" size={0}>
          <Tooltip title={row.teamId}>{teamLinks(row)}</Tooltip>
          {compact && (
            <>
              <Space size={4}>{snLozenge(row.sn)}</Space>
              {activityCell(row)}
            </>
          )}
        </Space>
      ),
    },
    {
      title: 'Device',
      key: 'device',
      responsive: ['md'],
      render: (row) => (
        <Space direction="vertical" size={0}>
          {snLozenge(row.sn)}
          {row.name && <Text type="secondary" style={{ fontSize: '11px' }}>{row.name}</Text>}
        </Space>
      ),
    },
    {
      title: 'State',
      key: 'state',
      render: (row) => stateCell(row),
    },
    {
      title: 'Activity',
      key: 'activity',
      responsive: ['md'],
      render: (row) => activityCell(row),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (row) => (
        <Space>
          <Tooltip title={compact ? 'Start/Restart Kiosk' : ''}>
            <Button
              size="small"
              icon={<PlayCircleOutlined />}
              loading={armingSn === row.sn}
              disabled={!isAdmin || !row.deviceTagId}
              onClick={() => onStartKiosk(row)}
            >
              {compact ? null : 'Start/Restart Kiosk'}
            </Button>
          </Tooltip>
          <a href={stripeReaderUrl(row)} target="_blank" rel="noopener noreferrer">
            <ExportOutlined />
            {compact ? null : ' Stripe'}
          </a>
        </Space>
      ),
    },
  ];

  /** Everything the grid no longer shows, exact times included — one click away. */
  const details = (row) => {
    const screen = row.readerActionType
      ? `${readerActionLabel(row.readerActionType)} ${readerActionState(row.readerActionStatus)}`.trim() : '—';
    const failure = row.lastArmFailedAt
      ? `${fmt(row.lastArmFailedAt)}${row.lastArmFailedBy ? ` by ${row.lastArmFailedBy}` : ''} — ${row.lastArmFailedReason || 'failed'}`
      : '—';
    const lines = [
      ['Reader', `${row.readerStatus || 'unknown'}${row.readerSeenAt ? ` · seen ${fmt(row.readerSeenAt)}` : ''}${row.readerCheckedAt ? ` · checked ${fmt(row.readerCheckedAt)}${row.readerCheckedBy ? ` by ${row.readerCheckedBy}` : ''}` : ''}`],
      ...(row.readerStatus === 'missing' ? [[
        'Not in Stripe',
        `the reader id on this tag (${row.tmr || '—'}) no longer exists in this Stripe account — rebind the serial to the team or take the tag off live; the sweep stops asking until the binding changes`,
      ]] : []),
      ['Screen', screen],
      ['Mode', `${row.mode === 'tip-kiosk' ? 'Always on' : 'Hand-off'} · ${row.status || '—'}`],
      ['Last armed', row.lastArmedAt ? `${fmt(row.lastArmedAt)}${row.lastArmedBy ? ` by ${row.lastArmedBy}` : ''}` : '—'],
      ['Last arm failure', failure],
      ['Ids', [row.deviceTagId, row.tmr, row.locationId].filter(Boolean).join(' · ') || '—'],
    ];
    return (
      <div data-testid="terminal-details" style={{ maxWidth: '640px', margin: '0 auto 12px', fontSize: '12px' }}>
        {lines.map(([label, value]) => (
          <div key={label} style={{ display: 'flex', gap: '12px' }}>
            <Text type="secondary" style={{ width: '120px', flex: 'none' }}>{label}</Text>
            <Text style={{ wordBreak: 'break-word' }}>{value}</Text>
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: '12px' }} />}
      <Space style={{ marginBottom: '12px' }} wrap>
        <Input.Search
          allowClear
          placeholder="Search team, serial or reader id"
          aria-label="Search terminals"
          onChange={(e) => setQuery(e.target.value)}
          style={{ width: '320px' }}
        />
        <Text type="secondary">
          {needle ? `${rows.length} of ${terminals.length} match · ` : ''}
          {fleetSummary(terminals)}
        </Text>
        <Tooltip title="Re-arm every Always-on terminal now — the 5-minute sweep, on demand. Screens in use are left alone.">
          <Button
            icon={<PlayCircleOutlined />}
            onClick={onSweepAll}
            loading={sweeping}
            disabled={!isAdmin}
          >
            Sweep all terminals
          </Button>
        </Tooltip>
      </Space>
      <Table
        rowKey="sn"
        loading={loading}
        dataSource={ordered}
        columns={columns}
        pagination={{ pageSize: 25 }}
        expandable={{
          expandedRowRender: (row) => (
            <>
              {details(row)}
              <TerminalHistoryRows serialNumber={row.sn} />
            </>
          ),
        }}
      />
    </>
  );
};

export default AdminAllTerminalsBody;
